import { useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { isTerminalJobState, type JobDTO, type JobLogDTO } from '@devrobot/shared';
import { api, seg } from '../../api/client';
import { fetchJobLogs, qk } from '../../api/queries';

export type StreamStatus = 'connecting' | 'streaming' | 'polling' | 'ended';

/**
 * Live job logs and state over SSE (`log`, `job`, `end` events). Duplicate log lines after an
 * automatic reconnect are dropped by sequence number. If the stream cannot be opened the hook
 * falls back to polling `/logs?after=` until the job is terminal.
 */
export function useJobStream(id: string): { logs: JobLogDTO[]; status: StreamStatus } {
  const qc = useQueryClient();
  const [logs, setLogs] = useState<JobLogDTO[]>([]);
  const [status, setStatus] = useState<StreamStatus>('connecting');

  useEffect(() => {
    setLogs([]);
    setStatus('connecting');
    let last = 0;
    let ended = false;
    let poll: ReturnType<typeof setInterval> | undefined;

    const append = (entries: JobLogDTO[]) => {
      const fresh = entries.filter((l) => l.seq > last);
      if (fresh.length === 0) return;
      last = fresh[fresh.length - 1].seq;
      setLogs((prev) => [...prev, ...fresh]);
    };

    const startPolling = () => {
      if (poll || ended) return;
      setStatus('polling');
      const tick = async () => {
        try {
          append(await fetchJobLogs(id, last));
          const job = await api.get<JobDTO>(`/api/v1/jobs/${seg(id)}`);
          qc.setQueryData(qk.job(id), job);
          if (isTerminalJobState(job.state)) {
            ended = true;
            setStatus('ended');
            if (poll) clearInterval(poll);
          }
        } catch {
          /* keep trying; the job page shows the last known state */
        }
      };
      void tick();
      poll = setInterval(tick, 3000);
    };

    if (typeof EventSource === 'undefined') {
      startPolling();
      return () => poll && clearInterval(poll);
    }

    const es = new EventSource(`/api/v1/jobs/${seg(id)}/stream`);
    es.onopen = () => setStatus('streaming');
    es.addEventListener('log', (ev) => {
      try {
        append([JSON.parse((ev as MessageEvent<string>).data) as JobLogDTO]);
      } catch {
        /* ignore */
      }
    });
    es.addEventListener('job', (ev) => {
      try {
        qc.setQueryData(qk.job(id), JSON.parse((ev as MessageEvent<string>).data) as JobDTO);
      } catch {
        /* ignore */
      }
    });
    es.addEventListener('end', () => {
      ended = true;
      setStatus('ended');
      es.close();
      void qc.invalidateQueries({ queryKey: qk.job(id) });
    });
    es.onerror = () => {
      if (ended) return;
      if (es.readyState === EventSource.CLOSED) startPolling();
      else setStatus('connecting');
    };

    return () => {
      es.close();
      if (poll) clearInterval(poll);
    };
  }, [id, qc]);

  return { logs, status };
}
